import type {
  EntityDetailSchema,
  ConfigEntityDetail,
} from '@web-config-app/core';
import { captureError } from '@leagueplatform/observability';
import { isProduction } from '@leagueplatform/app-environment';
import {
  getAllReferencedEntities,
  type EntityWithDependencies,
  type DiscoveryFailure,
} from '../../utilities/get-all-referenced-entities/get-all-referenced-entities';
import type { DeepPublishTreeEntry } from './use-deep-publish';

/**
 * Extracts common metadata fields from a config entity's attributes.
 */
export const getEntityMetadata = (entityDetail: ConfigEntityDetail) => {
  const metadata = entityDetail.attributes?.entityMetadata;
  return {
    domainEntityType: metadata?.domainEntityType ?? '',
    entityName: metadata?.name ?? entityDetail.id,
    domainEntityId: metadata?.domainEntityId,
    status: metadata?.status ?? 'draft',
  };
};

export interface DiscoverTreeParams {
  rootEntityData: Partial<ConfigEntityDetail>;
  schema: EntityDetailSchema;
  domainEntityType: string;
  loadEntityRelationshipsSchemas: Parameters<
    typeof getAllReferencedEntities
  >[0]['loadEntityRelationshipsSchemas'];
  findEntityOnSourceByDomainEntityId: Parameters<
    typeof getAllReferencedEntities
  >[0]['findEntityOnSourceByDomainEntityId'];
  fetchEntityOnSourceById: Parameters<
    typeof getAllReferencedEntities
  >[0]['fetchEntityOnSourceById'];
  getEntityDetailPath?: (
    entityType: string,
    instanceId: string,
  ) => string | undefined;
}

export interface DiscoverTreeResult {
  entries: DeepPublishTreeEntry[];
  failures: DiscoveryFailure[];
  entitiesMap: Map<string, EntityWithDependencies>;
  schemasMap: Map<string, EntityDetailSchema>;
  rootEntityId?: string;
}

/**
 * Discovers the full dependency tree for an entity by recursively following
 * all references. Returns the flat list of tree entries plus the raw maps
 * needed by the publish loop.
 */
export async function discoverDeepPublishTree({
  rootEntityData,
  schema,
  domainEntityType,
  loadEntityRelationshipsSchemas,
  findEntityOnSourceByDomainEntityId,
  fetchEntityOnSourceById,
  getEntityDetailPath,
}: DiscoverTreeParams): Promise<DiscoverTreeResult> {
  const {
    entities: entitiesMap,
    failures,
    schemas: discoveredSchemas,
  } = await getAllReferencedEntities({
    entityData: rootEntityData,
    entitySchema: schema,
    loadEntityRelationshipsSchemas,
    findEntityOnSourceByDomainEntityId,
    fetchEntityOnSourceById,
  });

  // Include root schema so the publish loop can look it up by type
  discoveredSchemas.set(domainEntityType, schema);

  if (isProduction() && failures.length > 0) {
    captureError(
      new Error(`Deep publish: ${failures.length} discovery failures`),
      {
        errorName: 'DeepPublishDiscoveryFailures',
        context: {
          DeepPublishDiscovery: {
            rootEntityId: rootEntityData.id,
            failureCount: String(failures.length),
          },
        },
      },
    );
  }

  // Build tree entries from the discovered entities
  const entries: DeepPublishTreeEntry[] = Array.from(entitiesMap.values()).map(
    ({ entity: discoveredEntity, dependencies }) => {
      const metadata = getEntityMetadata(discoveredEntity);
      const link = getEntityDetailPath?.(
        metadata.domainEntityType,
        discoveredEntity.id,
      );

      return {
        entity: discoveredEntity,
        dependencies,
        status: metadata.status,
        domainEntityType: metadata.domainEntityType,
        entityName: metadata.entityName,
        link,
      };
    },
  );

  return {
    entries,
    failures,
    entitiesMap,
    schemasMap: discoveredSchemas,
    rootEntityId: rootEntityData.id,
  };
}
